import React, { Suspense } from "react";
import Bugsnag from "@bugsnag/js";
import BugsnagPluginReact from "@bugsnag/plugin-react";
import ReactDOM from "react-dom/client";
import "@fontsource-variable/source-sans-3";
import "./assets/css/docket_styles.css";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import ErrorPage from "./ErrorPage";
import Login from "./features/onboarding/Login";
import Settings from "./features/settings/Settings";
import SettingsWebview from "./features/settings/SettingsWebview";
import About from "./features/settings/About";
import CovidPolicy from "./features/settings/CovidPolicy";
import Discover from "./features/discover/Discover";
import { Squirrel } from "./components/spinner/Squirrel";
import { loader as homeLoader } from "./features/home/Home";
import { createBrowserRouter, Outlet, RouterProvider } from "react-router-dom";

// import i18n (needs to be bundled ;))
import "./i18n";
import Records from "./features/immunizations/Records";
import SearchList from "./features/immunizations/SearchList";
import Home from "./features/home/Home";
import SearchForm from "./features/immunizations/SearchForm";
import SearchResults from "./features/immunizations/SearchResults";
import AltContactsForm from "./features/immunizations/AltContactsForm";
import EnterPinForm from "./features/immunizations/EnterPinForm";
import Events from "./features/immunizations/Events";
import AvatarList from "./features/immunizations/AvatarList";
import RequiresAuth from "./components/RequiresAuth";
import DefferedAction from "./features/sw_elements/DefferedAction";
import OfflineBanner from "./features/sw_elements/Offline";
import EnterPhonePin from "./features/onboarding/EnterPhonePin";
import VerifyPhone from "./features/onboarding/VerifyPhone";
import ReportList from "./features/immunizations/ReportList";
import { STAGE } from "./globals";
import VERSION from "./version";
import SignUpTemplate from "./features/onboarding/SignUpTemplate";
import { NotFound } from "./components/NotFound";
import AccountInfo from "./features/onboarding/AccountInfo";
import { CalendarAdd } from "./features/calendar-add";
import BrandingProvider from "./branding/BrandingProvider";
import ScreeningEvents from "./features/screening/Events";
import HighContrastWrapper from "./features/HighContrastWrapper";
import { initializeI18next } from "./i18n";
import { globalStore } from "./jotai/store";
import { Provider } from "jotai";
import SmartHealthLink from "./features/smart-health-links/link";
import LinkLogs from "./features/smart-health-links/LinkLogs";
import SmartHealthCard from "./features/smart-health-card/SmartHealthCard";
import MobilePhone from "./features/settings/MobilePhone";
import MobileNotifications from "./features/settings/MobileNotifications";
import DeepLinkListener from "./features/DeepLinkListener";
import BiometricsLockout from "./features/BiometricsLockout";
import NativeStatusBar from "./features/NativeStatusBar";
import { initializePushNotificationListeners } from "./pushNotifications";
import { Capacitor } from "@capacitor/core";

const getAppType = () => {
  const platform = Capacitor.getPlatform();
  if (platform === "ios") {
    return "iOS";
  } else if (platform === "android") {
    return "Android";
  }
  return "Browser";
};

Bugsnag.start({
  apiKey: "d521ee05a3871fc096427c05e2b6ceac",
  plugins: [new BugsnagPluginReact()],
  appVersion: VERSION,
  appType: getAppType(),
  redactedKeys: [/password/i, /token/i, /name/i, /dob/i, /phone/i, /title/i],
  releaseStage: STAGE,
});

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.register();
await initializeI18next();

await initializePushNotificationListeners();

const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <>
        <DeepLinkListener />
        <Login />
      </>
    ),
    errorElement: <NotFound />,
    action: async ({ params, request }) => {
      let formData = await request.formData();
      return {};
    },
  },
  {
    path: "/biometrics",
    element: <BiometricsLockout />,
  },
  {
    path: "/signin-link",
    element: <Login />,
  },
  {
    path: "/apple-auth/callback",
    element: <Login />,
  },
  {
    path: "/signup",
    element: (
      <RequiresAuth>
        <SignUpTemplate />
      </RequiresAuth>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        path: "accountInfo",
        element: <AccountInfo />,
      },
      {
        path: "verifyphone",
        element: <VerifyPhone autoFocusInput />,
      },
      {
        path: "enterpin",
        element: <EnterPhonePin autoFocusInput />,
      },
    ],
  },
  {
    path: "/add-event",
    element: <CalendarAdd />,
  },
  {
    path: "/home",
    element: (
      <RequiresAuth>
        <Home />
      </RequiresAuth>
    ),
    errorElement: <ErrorPage />,
    loader: homeLoader,
    children: [
      {
        path: "search",
        element: (
          <>
            <Outlet />
          </>
        ),
        children: [
          {
            index: true,
            element: <SearchList />,
          },
          {
            path: "newsearch",
            element: <SearchForm />,
          },
          {
            path: "altcontacts",
            element: <AltContactsForm />,
          },
          {
            path: "enterpin",
            element: <EnterPinForm />,
          },
          {
            path: "results",
            element: <SearchResults />,
          },
        ],
      },
      {
        path: "records",
        element: (
          <>
            <Outlet />
          </>
        ),
        children: [
          {
            index: true,
            element: <Records recordType="immunization" />,
          },
          {
            path: ":id/smart-health-link",
            element: (
              <>
                <Outlet />
              </>
            ),
            children: [
              {
                index: true,
                element: <SmartHealthLink />,
              },
              {
                path: "logs",
                element: <LinkLogs />,
              },
            ],
          },
          {
            path: ":id/avatar",
            element: <AvatarList />,
          },
          {
            path: ":id/smart-health-card",
            element: <SmartHealthCard />,
          },
          {
            path: "screening",
            element: <Records recordType="screening" />,
          },
          {
            path: "screeningevents",
            element: <ScreeningEvents />,
          },
          {
            path: "events",
            element: <Events />,
          },
          {
            path: "reports",
            element: <ReportList />,
          },
        ],
      },
      {
        path: "settings",
        element: (
          <>
            <Outlet />
          </>
        ),
        children: [
          {
            index: true,
            element: <Settings />,
          },
          {
            path: "phone",
            element: <MobilePhone />,
          },
          {
            path: "pin",
            element: (
              <div className="mt-20">
                <EnterPhonePin autoFocusInput />
              </div>
            ),
          },
          {
            path: "profile",
            element: (
              <div className="mt-6 mx-6">
                <AccountInfo />
              </div>
            ),
          },
          {
            path: "notifications",
            element: <MobileNotifications />,
          },
        ],
      },
      {
        path: "about",
        element: <About />,
      },
      {
        path: "covid-policy",
        element: <CovidPolicy />,
      },
      {
        path: "records-policy",
        element: <SettingsWebview url="https://dockethealth.com/faqs" />,
      },
      {
        path: "privacy",
        element: <SettingsWebview url="https://dockethealth.com/privacy_policy" />,
      },
      {
        path: "terms",
        element: <SettingsWebview url="https://dockethealth.com/terms_of_use" />,
      },
      {
        path: "feedback",
        element: <SettingsWebview url="https://www.surveymonkey.com/r/259TTMJ" />,
      },
      {
        path: "discover",
        element: <Discover />,
      },
    ],
  },
]);

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

// https://docs.bugsnag.com/platforms/javascript/react/
const ErrorBoundary = Bugsnag.getPlugin("react")!.createErrorBoundary(React);

function Loading() {
  return <Squirrel />;
}

root.render(
  <ErrorBoundary FallbackComponent={NotFound}>
    <React.StrictMode>
      <Provider store={globalStore}>
        <BrandingProvider>
          <Suspense fallback={<Loading />}>
            <DefferedAction />
            <OfflineBanner />
            <NativeStatusBar />
            <HighContrastWrapper>
              <RouterProvider router={router} fallbackElement={<Loading />} />
            </HighContrastWrapper>
          </Suspense>
        </BrandingProvider>
      </Provider>
    </React.StrictMode>
  </ErrorBoundary>
);
